import { isOrphanedArchivedSubTrack } from '../sort-tracks';
import type { TrackRow } from './types';

/**
 * Minimal shape the row builder reads off a track. Kept generic so both the
 * tRPC/Zero `TrackPreview` and lightweight test fixtures satisfy it, while the
 * built rows still carry the caller's full track object through for rendering.
 */
type RowTrack = Pick<TrackRow, 'id' | 'name' | 'parentTrackId'>;

/**
 * Whether `track` is a sub-track hidden by its parent's collapse toggle.
 * Shared by the row builder and the sidebar components (`Tracks`,
 * `ArchivedTracksSection`) so collapse semantics can't drift between them.
 */
export function isHiddenSubTrack<T extends RowTrack>(
  track: T,
  collapsedTrackIds: ReadonlySet<string>,
): boolean {
  return (
    track.parentTrackId != null && collapsedTrackIds.has(track.parentTrackId)
  );
}

/**
 * Expands a destination that had no sub-tracks before the move: it renders no
 * toggle, so a stale collapsed entry would hide the arriving sub-track.
 * Returns the new list, or null when nothing changes.
 */
export function expandEmptyMoveDestination(
  collapsedTrackIds: readonly string[],
  tracks: readonly Pick<TrackRow, 'parentTrackId'>[],
  destinationTrackId: string,
): string[] | null {
  const destinationHasSubtracks = tracks.some(
    (track) => track.parentTrackId === destinationTrackId,
  );
  if (
    destinationHasSubtracks ||
    !collapsedTrackIds.includes(destinationTrackId)
  ) {
    return null;
  }
  return collapsedTrackIds.filter((id) => id !== destinationTrackId);
}

export type TimelineTrackRow<T extends RowTrack> = {
  kind: 'track';
  index: number;
  track: T;
  isArchived: boolean;
  isSubTrack: boolean;
  // Currently only read by ArchivedTracksSection;
  hasSubtracks: boolean;
};

export type TimelineArchivedHeaderRow = {
  kind: 'archived-header';
  index: number;
  /**
   * How many groups the Archive section holds: one per archived parent track
   * (its cascaded sub-tracks belong to that group), plus one per still-active
   * parent whose sub-track was archived on its own. Not the number of archived
   * rows, and the active parents themselves are not archived — only the group
   * they label is counted.
   */
  archivedGroupCount: number;
};

export type TimelineAdditionalTeamsRow = {
  kind: 'additional-teams';
  index: number;
};

/**
 * Stands in for the parent row of an archived sub-track whose parent is still
 * active, so the Archive section can show which track the sub-track belongs to.
 * It carries no track of its own: the timeline draws no blocks for it, which is
 * what keeps the active parent's blocks from being drawn a second time.
 *
 * It is the group's collapse toggle, so it is emitted even while collapsed —
 * its sub-track rows are what disappears.
 */
export type TimelineGroupLabelRow = {
  kind: 'group-label';
  index: number;
  parentTrackId: string;
  parentName: string;
};

export type TimelineRow<T extends RowTrack> =
  | TimelineTrackRow<T>
  | TimelineArchivedHeaderRow
  | TimelineGroupLabelRow
  | TimelineAdditionalTeamsRow;

export type BuildTimelineRowsInput<T extends RowTrack> = {
  activeTracks: T[];
  /** The archived rows to render — scoped to the selected athlete. */
  archivedTracks: T[];
  /**
   * The complete archived list, used only to answer "is this sub-track's parent
   * archived?". `archivedTracks` is athlete-filtered, so an archived parent can
   * be missing from it while its child still renders — reading the orphan
   * predicate off the filtered list would make that child look like an orphan
   * and, with the flag off, drop it from the section entirely. Defaults to
   * `archivedTracks` where no wider list is available.
   */
  allArchivedTracks?: T[];
  collapsedTrackIds: Iterable<string>;
  // Parent ids whose Archive-section label is collapsed — see PlanContext for
  // why this is not `collapsedTrackIds`.
  collapsedGroupLabelIds: Iterable<string>;
  archivedSectionExpanded: boolean;
  hasAdditionalTeams: boolean;
  subTrackReorganizationFlag: boolean;
};

/**
 * Builds the single ordered list of timeline rows shared by the sidebar
 * (`TrackPanel`/`Tracks`) and the timeline (`BlockPanel`).
 */
export function buildTimelineRows<T extends RowTrack>({
  activeTracks,
  archivedTracks,
  allArchivedTracks = archivedTracks,
  collapsedTrackIds,
  collapsedGroupLabelIds,
  archivedSectionExpanded,
  hasAdditionalTeams,
  subTrackReorganizationFlag,
}: BuildTimelineRowsInput<T>): TimelineRow<T>[] {
  const collapsed = new Set(collapsedTrackIds);
  const collapsedGroupLabels = new Set(collapsedGroupLabelIds);

  const parentIdsWithChildren = (tracks: T[]): ReadonlySet<string> =>
    new Set(
      tracks
        .map((track) => track.parentTrackId)
        .filter((parentId): parentId is string => parentId != null),
    );
  // Both read the *rendered* lists, not `allArchivedTracks`: `hasSubtracks`
  // drives the collapse toggle, and a toggle that expands nothing because its
  // children were filtered out is worse than no toggle.
  const activeParentIds = parentIdsWithChildren(activeTracks);
  const archivedParentIds = parentIdsWithChildren(archivedTracks);

  const rows: TimelineRow<T>[] = [];
  let index = 0;

  const pushTrackRow = (
    track: T,
    isArchived: boolean,
    // A sub-track whose parent has no row in the same section is exempt from the
    // collapse set — see the archived loop below for why its parent's toggle
    // must not hide it.
    exemptFromCollapse = false,
  ) => {
    if (!exemptFromCollapse && isHiddenSubTrack(track, collapsed)) return;
    rows.push({
      kind: 'track',
      index: index++,
      track,
      isArchived,
      isSubTrack: track.parentTrackId != null,
      hasSubtracks: (isArchived ? archivedParentIds : activeParentIds).has(
        track.id,
      ),
    });
  };

  for (const track of activeTracks) {
    pushTrackRow(track, false);
  }

  // Flag off, an orphaned archived sub-track is dropped from the section
  // entirely — including from the header's existence check below. Phase 1
  // relaxed the server's archive guard unconditionally, so this state is
  // reachable with the flag off (archive a sub-track, then roll the flag back),
  // and the section must then render exactly as it did before the feature
  // existed: no label row, no orphan row, and no header over an empty section.
  const archivedTracksToRender = subTrackReorganizationFlag
    ? archivedTracks
    : archivedTracks.filter(
        (track) => !isOrphanedArchivedSubTrack(track, allArchivedTracks),
      );

  if (archivedTracksToRender.length > 0) {
    rows.push({
      kind: 'archived-header',
      index: index++,
      // Group key: a parent row is its own group; a cascaded child shares its
      // archived parent's; an orphaned sub-track forms the group of its
      // still-active parent. Counting only parent rows would read "Archived
      // Tracks (0)" over a section that visibly holds an orphaned sub-track.
      archivedGroupCount: new Set(
        archivedTracksToRender.map((track) => track.parentTrackId ?? track.id),
      ).size,
    });

    if (archivedSectionExpanded) {
      const activeTracksById = new Map(
        activeTracks.map((track) => [track.id, track]),
      );
      // The ids that own a row in this section — the only collapse toggles
      // available here.
      const renderedTrackIds = new Set(
        archivedTracksToRender.map((track) => track.id),
      );
      let labelledParentId: string | null = null;

      for (const track of archivedTracksToRender) {
        const parentId = track.parentTrackId;
        const isOrphan =
          parentId != null &&
          isOrphanedArchivedSubTrack(track, allArchivedTracks);

        // A collapsed parent hides its children, but only a parent with a row in
        // this section owns the toggle that would bring them back. The parent can
        // be missing for two reasons, and both leave its id in
        // `collapsedTrackIds` — archiving a parent adds it (see
        // TrackSidePanelItem): it is still active, so its toggle lives in the
        // active list and may be gone from there too once its last active
        // sub-track is archived; or it is archived but scoped out of this section
        // by the selected-athlete filter. Honouring the collapse either way would
        // drop the child and its blocks from both panels with nothing here to
        // reveal them, while the header still counted the group. So the collapse
        // set only ever hides a child whose parent renders in this section.
        const parentRendersHere =
          parentId != null && renderedTrackIds.has(parentId);
        const exemptFromCollapse = parentId != null && !parentRendersHere;

        // No parent row is left in this list to carry an orphan's group, so a
        // label row stands in for it, once per parent — `sortArchivedTracks`
        // keeps a parent's orphaned sub-tracks contiguous, so a repeat is a
        // duplicate.
        if (isOrphan && parentId !== labelledParentId) {
          const parent = activeTracksById.get(parentId);
          if (parent) {
            rows.push({
              kind: 'group-label',
              index: index++,
              parentTrackId: parentId,
              parentName: parent.name,
            });
            labelledParentId = parentId;
          }
        }

        // A collapsed group keeps its label — the toggle — and drops the rows
        // under it, the way a collapsed parent drops its children. Gated on
        // `labelledParentId`: a label that was never emitted (its parent is
        // missing from the active list) must not hide anything, or the orphan
        // would vanish from both panels with no toggle to bring it back.
        if (
          isOrphan &&
          parentId === labelledParentId &&
          collapsedGroupLabels.has(parentId)
        ) {
          continue;
        }

        pushTrackRow(track, true, exemptFromCollapse);
      }
    }
  }

  if (hasAdditionalTeams) {
    rows.push({ kind: 'additional-teams', index: index++ });
  }

  return rows;
}

/**
 * Whether the row at `index` should draw the divider between top-level track
 * groups (border-t). False when the preceding row isn't a track row — either
 * there is none (list start) or it's a section header, which has no border-b
 * of its own for this row to align with. Shared by `BlockPanel` and the
 * sidebar's `ArchivedTracksSection` so the rule isn't reasoned about twice.
 */
export function precededByTrackRow<T extends RowTrack>(
  rows: TimelineRow<T>[],
  index: number,
): boolean {
  return rows[index - 1]?.kind === 'track';
}

/**
 * Index of the row that should own the list's closing bottom border, or -1 if
 * no row should draw one.
 *
 * This must be the row that is genuinely last overall, not merely the last
 * `'track'`-kind row: a header row (e.g. "Archived Tracks (N)") is always
 * emitted once its section has any tracks, even while collapsed, and already
 * draws its own top divider (and, for archived, its own bottom border) —
 * so a track row it follows must never additionally draw border-b, or the
 * two borders stack into a visibly thicker line. Scoping "last track-kind
 * row" to whichever track row happens to be last (active vs. archived)
 * changes with expand/collapse state, which is exactly what made the
 * divider's thickness flicker between those states.
 *
 * A group label does own the border: collapsing one hides the rows under it,
 * so the label becomes last and would otherwise drop the closing border.
 */
export function getLastTrackRowIndex<T extends RowTrack>(
  rows: TimelineRow<T>[],
): number {
  const lastRow = rows[rows.length - 1];
  return lastRow?.kind === 'track' || lastRow?.kind === 'group-label'
    ? lastRow.index
    : -1;
}
